import React, { useState } from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { Feedback, PasswordField, PasswordFieldProps } from '@placer-ui/components';
import {
    isNumberPresent,
    isSpecialCharacterPresent,
    isUpperAndLowerCasePresent,
} from 'features/authentication/signup/validations/validations';

type PasswordFieldControllerProps<T extends FieldValues = FieldValues> = Omit<
    PasswordFieldProps,
    'onChange'
> &
    Required<Pick<UseControllerProps<T>, 'control'>> &
    UseControllerProps<T>;

export const PasswordFieldController = <T extends FieldValues = FieldValues>(
    props: PasswordFieldControllerProps<T>,
) => {
    const [isChanged, setIsChanged] = useState(false);
    const { control, rules, name, shouldUnregister, defaultValue, disabled, ...inputProps } = props;

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            disabled={disabled}
            render={({ field: { onChange, value, ...restField }, formState }) => {
                const errorMessage = formState.errors[restField.name]?.message as string;
                const isError = Boolean(errorMessage);
                const getState = (validation: boolean) => {
                    if (!isChanged) return 'guidance';
                    if (!validation) return 'error';
                    return 'success';
                };

                const upperAndLowercaseState = getState(isUpperAndLowerCasePresent(value));
                const oneNumberState = getState(isNumberPresent(value));
                const specialCharacterState = getState(isSpecialCharacterPresent(value));
                const minimumCharactersState = getState((value as string).length >= 10);

                const isGuidanceState = (state: string) => state === 'guidance';
                let feedback: Feedback[] | undefined;
                if (rules?.pattern) {
                    feedback = [
                        {
                            message: 'Uppercase and lowercase letter',
                            state: upperAndLowercaseState,
                            hideIcon: isGuidanceState(upperAndLowercaseState),
                        },
                        {
                            message: 'One number',
                            state: oneNumberState,
                            hideIcon: isGuidanceState(oneNumberState),
                        },
                        {
                            message: 'One special character',
                            state: specialCharacterState,
                            hideIcon: isGuidanceState(specialCharacterState),
                        },
                        {
                            message: 'At least 10 characters',
                            state: minimumCharactersState,
                            hideIcon: isGuidanceState(minimumCharactersState),
                        },
                    ];
                }

                const handleChange = (val: string) => {
                    onChange(val);
                };

                const showRequiredError = !rules?.pattern && !!errorMessage;

                return (
                    <PasswordField
                        {...restField}
                        {...inputProps}
                        value={value || ''}
                        onChange={(value) => {
                            if (!isChanged) setIsChanged(true);
                            handleChange(value);
                        }}
                        isError={isError}
                        feedback={feedback}
                        required={showRequiredError}
                    />
                );
            }}
        />
    );
};
